import { useState } from 'react';

interface ContentGeneratorProps {
  titleData: any;
  onContentGenerated: (data: any) => void;
  onBack: () => void;
}

export default function ContentGenerator({
  titleData,
  onContentGenerated,
  onBack,
}: ContentGeneratorProps) {
  const [contentSettings, setContentSettings] = useState({
    wordCount: '25000',
    writingStyle: 'Informative',
    includeImages: true,
    includeExamples: true,
    includeExercises: false,
    includeResources: true,
    personalExperience: false,
    caseStudies: false,
    chapterCount: '10',
  });

  const [isGenerating, setIsGenerating] = useState(false);
  const [isGeneratingOutline, setIsGeneratingOutline] = useState(false);
  const [generationStep, setGenerationStep] = useState<
    'settings' | 'outline' | 'generating' | 'complete'
  >('settings');
  const [outline, setOutline] = useState<any[]>([]);
  const [progress, setProgress] = useState(0);

  const [generatedContent, setGeneratedContent] = useState<{ [key: number]: string }>(
    {}
  );
  const [generatingChapters, setGeneratingChapters] = useState<{
    [key: number]: boolean;
  }>({});
  const [editingChapter, setEditingChapter] = useState<number | null>(null);
  const [editContent, setEditContent] = useState('');
  const [regeneratingTitles, setRegeneratingTitles] = useState<{
    [key: number]: boolean;
  }>({});
  const [regeneratingKeyPoint, setRegeneratingKeyPoint] = useState<{
    chapterIndex: number;
    pointIndex: number;
  } | null>(null);

  const wordCountOptions = [
    { value: '5000', label: '5,000 words (Short Guide)', pages: '~20 pages' },
    { value: '10000', label: '10,000 words (Mini eBook)', pages: '~40 pages' },
    { value: '15000', label: '15,000 words (Standard eBook)', pages: '~60 pages' },
    { value: '25000', label: '25,000 words (Comprehensive Guide)', pages: '~100 pages' },
    { value: '35000', label: '35,000 words (Detailed Manual)', pages: '~140 pages' },
    { value: '50000', label: '50,000+ words (Complete Book)', pages: '~200+ pages' },
  ];

  const writingStyles = [
    'Informative',
    'Conversational',
    'Academic',
    'Storytelling',
    'How-to Guide',
    'Case Study Based',
    'Question & Answer',
    'Step-by-Step',
  ];

  const handleSettingChange = (field: string, value: any) => {
    setContentSettings((prev) => ({ ...prev, [field]: value }));
  };

  const generateOutline = async () => {
    if (!titleData.title) {
      alert('Please generate a title first');
      return;
    }

    setIsGeneratingOutline(true);
    try {
      // Simulate AI generating chapter outline
      await new Promise(resolve => setTimeout(resolve, 2000));

      const numChapters = parseInt(contentSettings.chapterCount);
      const targetWords = parseInt(contentSettings.wordCount);

      const chapters: Chapter[] = Array.from({ length: numChapters }, (_, i) => {
        const chapterNumber = i + 1;
        let chapterTitle = '';
        
        // Generate unique chapter titles based on the book topic
        const bookTopic = titleData.title.toLowerCase();
        
        if (chapterNumber === 1) {
          chapterTitle = `Introduction to ${titleData.title.split(':')[0]}`;
        } else if (chapterNumber === numChapters) {
          chapterTitle = `Mastering ${titleData.title.split(':')[0]}: Next Steps`;
        } else {
          // Generate contextually relevant chapter titles
          const topicWords = bookTopic.split(' ').filter(w => w.length > 3);
          const mainTopic = topicWords[Math.min(i % topicWords.length, topicWords.length - 1)] || 'Advanced Topics';
          chapterTitle = `${mainTopic.charAt(0).toUpperCase() + mainTopic.slice(1)} Fundamentals and Applications`;
        }

        // Generate unique key points for each chapter based on its title
        const generateUniqueKeyPoints = (title: string, chapterNum: number): string[] => {
          const titleLower = title.toLowerCase();
          const points: string[] = [];
          
          // Extract main topic from chapter title
          const mainWords = title.split(' ').filter(w => 
            w.length > 3 && 
            !['chapter', 'introduction', 'mastering', 'next', 'steps', 'fundamentals', 'applications'].includes(w.toLowerCase())
          );
          
          const topic = mainWords[0] || 'topic';
          
          if (chapterNum === 1) {
            points.push(
              `Historical background and evolution of ${topic}`,
              `Core principles and foundational concepts`,
              `Why ${topic} matters in today's context`,
              `Key terminology and definitions`,
              `Overview of what you'll learn in this book`
            );
          } else if (chapterNum === numChapters) {
            points.push(
              `Advanced strategies for ${topic} mastery`,
              `Real-world case studies and success stories`,
              `Future trends and emerging developments`,
              `Building your action plan`,
              `Resources for continued learning`
            );
          } else {
            // Generate varied points for middle chapters
            const pointTypes = [
              `Understanding ${topic} in depth`,
              `Practical techniques for implementing ${topic}`,
              `Common challenges and how to overcome them`,
              `Best practices from industry experts`,
              `Measuring success and tracking progress`,
              `Tools and resources for ${topic}`,
              `Case studies: ${topic} in action`,
              `Advanced ${topic} strategies`,
              `Integrating ${topic} with other approaches`,
              `Troubleshooting common ${topic} issues`
            ];
            
            // Select 5 unique points for this chapter
            const startIndex = ((chapterNum - 2) * 3) % pointTypes.length;
            for (let j = 0; j < 5; j++) {
              points.push(pointTypes[(startIndex + j) % pointTypes.length]);
            }
          }
          
          return points;
        };

        return {
          id: `chapter-${chapterNumber}`,
          number: chapterNumber,
          title: chapterTitle,
          keyPoints: generateUniqueKeyPoints(chapterTitle, chapterNumber),
          estimatedWords: Math.floor(targetWords / numChapters)
        };
      });

      setOutline(chapters);
      setGenerationStep('outline');
    } catch (error) {
      console.error('Error generating outline:', error);
      alert('Failed to generate outline. Please try again.');
    } finally {
      setIsGeneratingOutline(false);
    }
  };

  const getChapterTitle = (topic: string, chapterNum: number, totalChapters: number) => {
    const titles = [
      `Introduction to ${topic}`,
      `Understanding the Fundamentals`,
      `Getting Started: First Steps`,
      `Core Concepts and Principles`,
      `Advanced Techniques and Strategies`,
      `Common Challenges and Solutions`,
      `Best Practices and Tips`,
      `Real-World Applications`,
      `Advanced Implementation`,
      `Future Trends and Conclusion`,
    ];

    if (chapterNum === 1) return `Introduction to ${topic}`;
    if (chapterNum === totalChapters) return `Mastering ${topic}: Next Steps`;

    return titles[chapterNum - 1] || `Chapter ${chapterNum}: Advanced ${topic}`;
  };

  const getChapterKeyPoints = (topic: string, chapterNum: number, totalChapters: number) => {
    const basePoints = [
      `Key concepts and definitions`,
      `Practical applications`,
      `Step-by-step instructions`,
      `Common mistakes to avoid`,
      `Expert tips and insights`,
    ];

    return basePoints.slice(0, 3 + Math.floor(Math.random() * 3));
  };

  const generateChapterContent = async (chapterIndex: number, isRewrite: boolean = false) => {
    setGeneratingChapters((prev) => ({ ...prev, [chapterIndex]: true }));

    if (isRewrite) {
      setGeneratedContent((prev) => ({ ...prev, [chapterIndex]: '' }));
    }

    const chapterTitle = outline[chapterIndex]?.title || `Chapter ${chapterIndex + 1}`;
    
    // Calculate words per chapter based on selected target word count
    const targetWords = parseInt(contentSettings.wordCount);
    const totalChapters = outline.length;
    const wordsPerChapter = Math.floor(targetWords / totalChapters);

    // Distribute words across sections proportionally
    const totalSectionWords = wordsPerChapter;
    const sections = [
      { title: 'Introduction', words: Math.floor(totalSectionWords * 0.15) },
      { title: 'Understanding Core Concepts', words: Math.floor(totalSectionWords * 0.18) },
      { title: 'Key Principles and Foundations', words: Math.floor(totalSectionWords * 0.17) },
      { title: 'Step-by-Step Implementation Guide', words: Math.floor(totalSectionWords * 0.20) },
      { title: 'Execution and Monitoring', words: Math.floor(totalSectionWords * 0.12) },
      { title: 'Common Challenges and Solutions', words: Math.floor(totalSectionWords * 0.10) },
      { title: 'Expert Tips and Best Practices', words: Math.floor(totalSectionWords * 0.08) },
    ];

    let fullContent = '';

    for (const section of sections) {
      const sectionContent = `${section.title}\n\n${generateParagraph(section.words, chapterTitle, section.title)}\n\n`;

      const words = sectionContent.split(' ');
      for (let i = 0; i < words.length; i++) {
        await new Promise((resolve) => setTimeout(resolve, 30));
        fullContent += (i === 0 ? '' : ' ') + words[i];
        setGeneratedContent((prev) => ({ ...prev, [chapterIndex]: fullContent }));
      }
    }

    setGeneratingChapters((prev) => ({ ...prev, [chapterIndex]: false }));
  };

  const generateParagraph = (wordCount: number, chapterTitle: string, sectionTitle: string): string => {
    const words = [
      'effective',
      'strategic',
      'comprehensive',
      'innovative',
      'practical',
      'essential',
      'fundamental',
      'critical',
      'important',
      'valuable',
      'significant',
      'powerful',
      'approach',
      'method',
      'technique',
      'strategy',
      'process',
      'system',
      'framework',
      'understanding',
      'implementation',
      'development',
      'optimization',
      'enhancement',
      'success',
      'growth',
      'improvement',
      'achievement',
      'results',
      'outcomes',
      'business',
      'organization',
      'team',
      'project',
      'goal',
      'objective',
      'target',
      'analysis',
      'evaluation',
      'assessment',
      'review',
      'examination',
      'study',
      'solution',
      'answer',
      'resolution',
      'response',
      'action',
      'initiative',
    ];

    let paragraph = '';
    let currentWordCount = 0;

    while (currentWordCount < wordCount) {
      const sentenceLength = Math.floor(Math.random() * 15) + 10;
      let sentence = '';

      for (let i = 0; i < sentenceLength && currentWordCount < wordCount; i++) {
        const word = words[Math.floor(Math.random() * words.length)];
        sentence += (i === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word) + ' ';
        currentWordCount++;
      }

      paragraph += sentence.trim() + '. ';
    }

    return paragraph.trim();
  };

  const handleEditChapter = (chapterIndex: number) => {
    setEditingChapter(chapterIndex);
    setEditContent(generatedContent[chapterIndex] || '');
  };

  const handleSaveEdit = () => {
    if (editingChapter !== null) {
      setGeneratedContent((prev) => ({ ...prev, [editingChapter]: editContent }));
      setEditingChapter(null);
      setEditContent('');
    }
  };

  const handleCancelEdit = () => {
    setEditingChapter(null);
    setEditContent('');
  };

  const generateContent = async () => {
    setGenerationStep('generating');
    setProgress(0);

    // Simulate content generation with progress
    for (let i = 0; i <= 100; i += 10) {
      await new Promise((resolve) => setTimeout(resolve, 300));
      setProgress(i);
    }

    // Generate table of contents
    const tableOfContents = {
      title: 'Table of Contents',
      entries: outline.map((chapter, index) => ({
        chapterNumber: index + 1,
        chapterTitle: chapter.title,
        pageNumber: 3 + index * 2,
      })),
    };

    // Calculate actual words per chapter based on selected target word count
    const targetWords = parseInt(contentSettings.wordCount);
    const wordsPerChapter = Math.floor(targetWords / outline.length);

    // Assemble final ebook data
    const fullContent = {
      title: titleData.title,
      subtitle: titleData.subtitle,
      template: titleData.template,
      settings: contentSettings,
      outline: outline,
      tableOfContents: tableOfContents,
      // For simplicity, we store empty strings for chapter content here.
      // Detailed content generation is handled chapter‑by‑chapter via the UI.
      chapters: outline.map((chapter, index) => ({
        ...chapter,
        content: '',
        wordCount: wordsPerChapter,
      })),
      totalWordCount: targetWords,
      metadata: {
        topic: titleData.topic,
        targetAudience: titleData.targetAudience,
        tone: titleData.tone,
        keywords: titleData.keywords,
        generatedAt: new Date().toISOString(),
      },
    };

    setGenerationStep('complete');
    onContentGenerated(fullContent);
  };

  const regenerateKeyPoint = async (chapterIndex: number, pointIndex: number) => {
    if (!outline || !outline[chapterIndex]) return;

    setRegeneratingKeyPoint({ chapterIndex, pointIndex });

    // Placeholder logic for new point generation (shuffle words)
    const currentPoint = outline[chapterIndex].keyPoints[pointIndex];
    const shuffled = currentPoint
      .split(' ')
      .sort(() => Math.random() - 0.5)
      .join(' ');
    const updatedOutline = [...outline];
    updatedOutline[chapterIndex].keyPoints[pointIndex] = shuffled;
    setOutline(updatedOutline);

    setRegeneratingKeyPoint(null);
  };

  const regenerateChapterTitle = async (chapterIndex: number) => {
    if (!titleData.title) return;

    setRegeneratingTitles((prev) => ({ ...prev, [chapterIndex]: true }));

    // Placeholder for new title generation
    const newTitle = `${titleData.title} - Revised Chapter ${chapterIndex + 1}`;
    const updatedOutline = [...outline];
    updatedOutline[chapterIndex].title = newTitle;
    setOutline(updatedOutline);

    // Clear any existing generated content for this chapter
    setGeneratedContent((prev) => {
      const newContent = { ...prev };
      newContent[chapterIndex] = '';
      return newContent;
    });

    setRegeneratingTitles((prev) => ({ ...prev, [chapterIndex]: false }));
  };

  if (generationStep === 'complete') {
    return null; // Handled by parent component
  }

  if (generationStep === 'generating') {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-8">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-loader-4-line animate-spin text-3xl text-blue-600"></i>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Generating Your eBook Content
            </h1>
            <p className="text-gray-600">
              Our AI is creating high-quality content based on your specifications...
            </p>
          </div>

          <div className="mb-8">
            <div className="flex justify-between text-sm text-gray-600 mb-2">
              <span>Progress</span>
              <span>{progress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="text-center">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
                <i className="ri-check-line text-green-600 text-xl"></i>
              </div>
              <h3 className="font-medium text-gray-900">Outline Created</h3>
              <p className="text-sm text-gray-600">{outline.length} chapters planned</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
                <i className="ri-loader-4-line animate-spin text-blue-600 text-xl"></i>
              </div>
              <h3 className="font-medium text-gray-900">Generating Content</h3>
              <p className="text-sm text-gray-600">Creating detailed chapters</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2">
                <i className="ri-file-text-line text-gray-400 text-xl"></i>
              </div>
              <h3 className="font-medium text-gray-500">Final Review</h3>
              <p className="text-sm text-gray-500">Quality check pending</p>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-2">Generation Details</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Target Length:</span>
                <span className="ml-2 font-medium">{outline.length * 500} words (500 per chapter)</span>
              </div>
              <div>
                <span className="text-gray-600">Writing Style:</span>
                <span className="ml-2 font-medium">{contentSettings.writingStyle}</span>
              </div>
              <div>
                <span className="text-gray-600">Chapters:</span>
                <span className="ml-2 font-medium">{outline.length}</span>
              </div>
              <div>
                <span className="text-gray-600">Estimated Pages:</span>
                <span className="ml-2 font-medium">
                  ~{Math.ceil((outline.length * 500) / 250) + 2} (includes index)
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (generationStep === 'outline') {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-8">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-list-check text-3xl text-green-600"></i>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Chapter Outline Generated</h1>
            <p className="text-gray-600">
              Review your eBook structure before we generate the full content.
            </p>
          </div>

          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">{titleData.title}</h2>
            {titleData.subtitle && (
              <p className="text-lg text-gray-600 mb-6">{titleData.subtitle}</p>
            )}

            <div className="space-y-4">
              {outline.map((chapter, index) => (
                <div key={chapter.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-gray-900 flex-1">
                      Chapter {chapter.number}: {chapter.title}
                    </h3>
                    <div className="flex items-center gap-3 ml-4">
                      <span className="text-sm text-gray-500 whitespace-nowrap">
                        ~{chapter.estimatedWords.toLocaleString()} words
                      </span>
                      {generatedContent[index] ? (
                        <button
                          onClick={() => generateChapterContent(index, true)}
                          disabled={generatingChapters[index]}
                          className="px-3 py-1.5 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                        >
                          {generatingChapters[index] ? (
                            <>
                              <i className="ri-loader-4-line animate-spin"></i>
                              Regenerating...
                            </>
                          ) : (
                            <>
                              <i className="ri-refresh-line"></i>
                              Regenerate
                            </>
                          )}
                        </button>
                      ) : null}
                    </div>
                  </div>
                  <ul className="text-sm text-gray-600 space-y-1 mb-3">
                    {chapter.keyPoints.map((point: string, pointIndex: number) => (
                      <li key={pointIndex} className="flex items-start group">
                        <span className="text-blue-600 mr-2">•</span>
                        <span className="flex-1">{point}</span>
                        <button
                          onClick={() => regenerateKeyPoint(index, pointIndex)}
                          disabled={
                            regeneratingKeyPoint?.chapterIndex === index &&
                            regeneratingKeyPoint?.pointIndex === pointIndex
                          }
                          className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity px-2 py-1 text-xs bg-purple-50 text-purple-600 rounded hover:bg-purple-100 disabled:opacity-50 flex items-center gap-1"
                          title="Regenerate this key point"
                        >
                          {regeneratingKeyPoint?.chapterIndex === index &&
                          regeneratingKeyPoint?.pointIndex === pointIndex ? (
                            <>
                              <i className="ri-loader-4-line animate-spin"></i>
                              <span>Regenerating...</span>
                            </>
                          ) : (
                            <>
                              <i className="ri-refresh-line"></i>
                              <span>Regenerate</span>
                            </>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>

                  {/* Chapter Content Preview */}
                  {editingChapter === index ? (
                    <div className="mt-4 border-t border-gray-200 pt-4">
                      <div className="mb-2 flex items-center justify-between">
                        <label className="text-sm font-medium text-gray-700">
                          Edit Chapter Content
                        </label>
                        <span className="text-xs text-gray-500">
                          {editContent.split(' ').filter((w) => w).length} words
                        </span>
                      </div>
                      <textarea
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        className="w-full h-64 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        placeholder="Edit your chapter content here..."
                      />
                      <div className="flex gap-2 mt-3">
                        <button
                          onClick={handleSaveEdit}
                          className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap"
                        >
                          <i className="ri-check-line mr-1"></i>
                          Save Changes
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="px-4 py-2 bg-gray-500 text-white text-sm rounded-lg hover:bg-gray-600 transition-colors whitespace-nowrap"
                        >
                          <i className="ri-close-line mr-1"></i>
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : generatedContent[index] ? (
                    <div className="mt-4 border-t border-gray-200 pt-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">
                          Chapter Preview
                        </span>
                        <button
                          onClick={() => handleEditChapter(index)}
                          className="px-3 py-1.5 bg-gray-600 text-white text-sm rounded-lg hover:bg-gray-700 transition-colors whitespace-nowrap"
                        >
                          <i className="ri-edit-line mr-1"></i>
                          Edit
                        </button>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4 max-h-64 overflow-y-auto">
                        <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap">
                          {generatedContent[index]}
                          {generatingChapters[index] && (
                            <span className="inline-block w-2 h-4 bg-blue-600 animate-pulse ml-1"></span>
                          )}
                        </div>
                      </div>
                      <div className="mt-2 text-xs text-gray-500">
                        {generatedContent[index].split(' ').filter((w) => w).length} words
                      </div>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          </div>

          <div className="bg-blue-50 rounded-lg p-4 mb-8">
            <h3 className="font-semibold text-blue-900 mb-2">Content Summary</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-blue-700">Total Chapters:</span>
                <div className="font-semibold text-blue-900">{outline.length}</div>
              </div>
              <div>
                <span className="text-blue-700">Total Words:</span>
                <div className="font-semibold text-blue-900">
                  {parseInt(contentSettings.wordCount).toLocaleString()}
                </div>
              </div>
              <div>
                <span className="text-blue-700">Estimated Pages:</span>
                <div className="font-semibold text-blue-900">
                  ~{Math.ceil(parseInt(contentSettings.wordCount) / 250) + 2}
                </div>
              </div>
              <div>
                <span className="text-blue-700">Words/Chapter:</span>
                <div className="font-semibold text-blue-900">
                  {Math.floor(parseInt(contentSettings.wordCount) / outline.length).toLocaleString()}
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-between">
            <button
              onClick={() => setGenerationStep('settings')}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              Modify Settings
            </button>
            <button
              onClick={generateContent}
              className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
            >
              Generate Full Content
              <i className="ri-arrow-right-line ml-2"></i>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Settings step UI (unchanged)
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-8">
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-settings-3-line text-3xl text-blue-600"></i>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Content Generation Settings
          </h1>
          <p className="text-gray-600">
            Customize your eBook content to match your specific needs and
            preferences.
          </p>
        </div>

        {/* Title Info */}
        <div className="bg-gray-50 rounded-lg p-4 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-1">
            "{titleData.title}"
          </h2>
          {titleData.subtitle && (
            <p className="text-gray-600 mb-2">{titleData.subtitle}</p>
          )}
          <div className="flex flex-wrap gap-4 text-sm text-gray-500">
            <span>Topic: {titleData.topic}</span>
            <span>Audience: {titleData.targetAudience}</span>
            <span>Tone: {titleData.tone}</span>
          </div>
        </div>

        <form className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Target Word Count
              </label>
              <select
                value={contentSettings.wordCount}
                onChange={(e) => handleSettingChange('wordCount', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-8"
              >
                {wordCountOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label} ({option.pages})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Writing Style
              </label>
              <select
                value={contentSettings.writingStyle}
                onChange={(e) => handleSettingChange('writingStyle', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-8"
              >
                {writingStyles.map((style) => (
                  <option key={style} value={style}>
                    {style}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-4">
              Number of Chapters
            </label>
            <select
              value={contentSettings.chapterCount}
              onChange={(e) => handleSettingChange('chapterCount', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-8"
            >
              {Array.from({ length: 15 }, (_, i) => i + 5).map((num) => {
                const targetWords = parseInt(contentSettings.wordCount);
                const wordsPerChapter = Math.round(targetWords / num);
                return (
                  <option key={num} value={num.toString()}>
                    {num} chapters ({wordsPerChapter.toLocaleString()} words each = {targetWords.toLocaleString()} total words)
                  </option>
                );
              })}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-4">
              Content Elements to Include
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { key: 'includeImages', label: 'Image placeholders and descriptions' },
                { key: 'includeExamples', label: 'Real-world examples and case studies' },
                { key: 'includeExercises', label: 'Exercises and action items' },
                { key: 'includeResources', label: 'Additional resources and references' },
                { key: 'personalExperience', label: 'Personal experience sections' },
                { key: 'caseStudies', label: 'Detailed case study analysis' },
              ].map((item) => (
                <label key={item.key} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={contentSettings[item.key as keyof typeof contentSettings] as boolean}
                    onChange={(e) => handleSettingChange(item.key, e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-3"
                  />
                  <span className="text-sm text-gray-700">{item.label}</span>
                </label>
              ))}
            </div>
          </div>
        </form>

        <div className="mt-8 p-4 bg-blue-50 rounded-lg">
          <h3 className="font-semibold text-blue-900 mb-2">Content Preview</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-blue-700">Estimated Pages:</span>
              <div className="font-semibold text-blue-900">
                ~{Math.ceil(parseInt(contentSettings.wordCount) / 250) + 2}
              </div>
            </div>
            <div>
              <span className="text-blue-700">Chapters:</span>
              <div className="font-semibold text-blue-900">{contentSettings.chapterCount}</div>
            </div>
            <div>
              <span className="text-blue-700">Total Words:</span>
              <div className="font-semibold text-blue-900">
                {parseInt(contentSettings.wordCount).toLocaleString()}
              </div>
            </div>
            <div>
              <span className="text-blue-700">Reading Time:</span>
              <div className="font-semibold text-blue-900">
                ~{Math.ceil(parseInt(contentSettings.wordCount) / 200)} min
              </div>
            </div>
          </div>
          <div className="mt-3 text-sm text-blue-800">
            <i className="ri-information-line mr-1"></i>
            Includes cover page + table of contents + {contentSettings.chapterCount} chapters 
            ({Math.floor(parseInt(contentSettings.wordCount) / parseInt(contentSettings.chapterCount)).toLocaleString()} words each)
          </div>
        </div>

        <div className="mt-8 flex justify-between">
          <button
            onClick={onBack}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
          >
            <i className="ri-arrow-left-line mr-2"></i>
            Back to Title
          </button>
          <button
            onClick={generateOutline}
            className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
          >
            Create Outline
            <i className="ri-arrow-right-line ml-2"></i>
          </button>
        </div>
      </div>
    </div>
  );
}